import { useState, useRef, useEffect ,useId} from "react"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { BASE_URL, getImageUrl } from "../../apiConfig"
import { useAuth } from "../../context/AuthProvider";
import axios from "axios"
import { Trash2 } from "lucide-react"
import Select from "react-select"


export default function UpdateProductForm({
  productData,
  categories,
  availableClients,
  availableVariants,
  Fournisseurs,
}) {
  const product = productData?.data || productData
  const { token } = useAuth();

  const [formData, setFormData] = useState({
    name: product?.name || "",
    description: product?.description || "",
    achat_price: product?.price?.achat_price || "",
    saller_price: product?.price?.saller_price || "",
    selling_price: product?.price?.selling_price || "",
    stock: product?.price?.stock || "",
    status: product?.status || "active",
    is_global: product?.is_global === 1 || true,
    variant: product?.variants?.length > 0 || false,
    category_id: product?.category_id || "",
    fournisseur_id: product?.fournisseur_id || "",
    image_urls: product?.image_urls || "",
    product_expired_soon: product?.product_expired_soon === 1 || false,
    discount: product?.discount?.value || "",
    discount_start_date: product?.discount?.date_start || "",
    discount_end_date: product?.discount?.date_end || "",
    rating: product?.rating || 0,
  })

  // Initialize primary image
  const [primaryImage, setPrimaryImage] = useState(() => {
    if (product?.image_py) {
      return {
        name: product.image_py,
        preview: getImageUrl(product.image_py),
        isExisting: true,
      }
    }
    return null
  })

  // Track if primary image was removed
  const [primaryImageRemoved, setPrimaryImageRemoved] = useState(false)

  // Initialize discount state
  const [has_discount, setHas_discount] = useState(() => {
    return Boolean(product?.discount?.status === "true" || product?.discount?.status === true)
  })

  // Initialize additional images
  const [images, setImages] = useState(() => {
    if (product?.images && Array.isArray(product.images)) {
      return product.images.map((img) => ({
        id: img.id,
        name: img.image,
        preview: getImageUrl(img.image),
        isExisting: true,
      }))
    }
    return []
  })

  // Track which existing images are marked for deletion
  const [deletedImageIds, setDeletedImageIds] = useState([])
  const selectId = useId();


  const [variantGroups, setVariantGroups] = useState(() => {
    if (product?.variants?.length > 0) {
      // Extract variant types from the first group to ensure consistency
      const firstGroupTypes = product.variants[0].details.map((detail) => detail.variant.type)

      return product.variants.map((variant) => {
        const selectedVariants = {}
        variant.details.forEach((detail) => {
          if (firstGroupTypes.includes(detail.variant.type)) {
            selectedVariants[detail.variant.type] = detail.variant_id
          }
        })
        return {
          id: variant.id,
          selectedVariants,
        }
      })
    }
    return [{ id: Date.now(), selectedVariants: {} }]
  })

  // Track selected variant types to maintain consistency
  const [selectedVariantTypes, setSelectedVariantTypes] = useState(() => {
    if (product?.variants?.length > 0) {
      return [...new Set(product.variants[0].details.map((detail) => detail.variant.type))]
    }
    return []
  })

  // Initialize selected clients
  const [selectedClients, setSelectedClients] = useState(() => {
    return product?.sallers ? product.sallers.map((s) => s.id) : []
  })

  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [success, setSuccess] = useState(false)
  const [dragActivePrimary, setDragActivePrimary] = useState(false)
  const [dragActiveAdditional, setDragActiveAdditional] = useState(false)
  const primaryFileInputRef = useRef(null)
  const additionalFileInputRef = useRef(null)

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    })
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" })
    }
  }

  const handlePrimaryImageUpload = (files) => {
    if (files && files.length > 0) {
      const file = files[0]
      if (file.type.startsWith("image/")) {
        // If replacing an existing image, mark it for removal
        if (primaryImage?.isExisting) {
          setPrimaryImageRemoved(true)
        }
        setPrimaryImage({ file, preview: URL.createObjectURL(file), isExisting: false })
        setErrors((prev) => ({ ...prev, primaryImage: "" }))
      } else {
        setPrimaryImage(null)
        setErrors((prev) => ({ ...prev, primaryImage: "The uploaded file must be an image." }))
        toast.error("Primary image must be an image (PNG, JPG, GIF).")
      }
    } else {
      setPrimaryImage(null)
    }
  }

  const removePrimaryImage = () => {
    if (primaryImage?.isExisting) {
      setPrimaryImageRemoved(true)
    }
    setPrimaryImage(null)
    setErrors((prev) => ({ ...prev, primaryImage: "" }))
  }

  const handleAdditionalImageUpload = (files) => {
    if (files && files.length > 0) {
      const newValidImages = Array.from(files)
        .filter((file) => {
          if (file.type.startsWith("image/")) {
            return true
          } else {
            toast.error(`File "${file.name}" is not an image and was skipped.`)
            return false
          }
        })
        .map((file) => ({
          id: `new-${Date.now()}-${Math.random()}`,
          file,
          preview: URL.createObjectURL(file),
          isExisting: false,
        }))
      setImages((prevImages) => [...prevImages, ...newValidImages])
    }
  }

  const removeImage = (id) => {
    const imageToRemove = images.find((img) => img.id === id)
    if (imageToRemove?.isExisting) {
      setDeletedImageIds((prev) => [...prev, imageToRemove.id])
    }
    setImages(images.filter((img) => img.id !== id))
  }

  const handleDragPrimary = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActivePrimary(true)
    } else if (e.type === "dragleave") {
      setDragActivePrimary(false)
    }
  }

  const handleDropPrimary = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActivePrimary(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handlePrimaryImageUpload(e.dataTransfer.files)
    }
  }

  const handleDragAdditional = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActiveAdditional(true)
    } else if (e.type === "dragleave") {
      setDragActiveAdditional(false)
    }
  }

  const handleDropAdditional = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActiveAdditional(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleAdditionalImageUpload(e.dataTransfer.files)
    }
  }

  const addVariantType = (type) => {
    setSelectedVariantTypes((prev) => [...prev, type])

    // Add this type to all existing groups
    setVariantGroups((prevGroups) =>
      prevGroups.map((group) => ({
        ...group,
        selectedVariants: {
          ...group.selectedVariants,
          [type]: "",
        },
      })),
    )
  }

  const removeVariantType = (type) => {
    setSelectedVariantTypes((prev) => prev.filter((t) => t !== type))

    // Remove this type from all groups
    setVariantGroups((prevGroups) =>
      prevGroups.map((group) => {
        const newSelectedVariants = { ...group.selectedVariants }
        delete newSelectedVariants[type]
        return {
          ...group,
          selectedVariants: newSelectedVariants,
        }
      }),
    )
  }

  const addGroup = () => {
    const newSelectedVariants = {}
    selectedVariantTypes.forEach((type) => {
      newSelectedVariants[type] = ""
    })

    setVariantGroups((prev) => [
      ...prev,
      {
        id: Date.now(),
        selectedVariants: newSelectedVariants,
      },
    ])
  }

  const removeGroup = (id) => {
    setVariantGroups((prev) => prev.filter((group) => group.id !== id))
  }

  const updateGroupVariant = (groupId, type, variantId) => {
    setVariantGroups((prev) =>
      prev.map((group) =>
        group.id === groupId ? { ...group, selectedVariants: { ...group.selectedVariants, [type]: variantId } } : group,
      ),
    )
  }

  const toggleClient = (clientId) => {
    setSelectedClients((prev) => (prev.includes(clientId) ? prev.filter((id) => id !== clientId) : [...prev, clientId]))
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) newErrors.name = "Product name is required"
    if (!formData.achat_price) newErrors.achat_price = "Achat price is required"
    if (!formData.saller_price) newErrors.saller_price = "Reseller price is required"
    if (!formData.selling_price) newErrors.selling_price = "Selling price price is required"
    if (!formData.stock) newErrors.stock = "Stock quantity is required"
    if (!formData.category_id) newErrors.category_id = "Category is required"
    if (!formData.fournisseur_id) newErrors.fournisseur_id = "Fournisseur is required"
    if (!primaryImage) newErrors.primaryImage = "A primary image is required."

    if (
      !formData.achat_price ||
      !formData.saller_price ||
      !formData.selling_price ||
      Number.parseFloat(formData.saller_price) <= Number.parseFloat(formData.achat_price) ||
      Number.parseFloat(formData.selling_price) <= Number.parseFloat(formData.saller_price) ||
      Number.parseFloat(formData.selling_price) <= Number.parseFloat(formData.achat_price)
    ) {
      newErrors.price = "Achat price must be less than selling price and saller price"
    }

    if (!formData.is_global && selectedClients.length === 0) {
      newErrors.clients = "At least one client must be selected when product is not global"
    }

    if (has_discount) {
      if (!formData.discount) newErrors.discount = "Discount is required"
      if (!formData.discount_start_date) newErrors.discount_start_date = "Discount start date is required"
      if (!formData.discount_end_date) newErrors.discount_end_date = "Discount end date is required"
      if (
        formData.discount_start_date &&
        formData.discount_end_date &&
        new Date(formData.discount_start_date) >= new Date(formData.discount_end_date)
      ) {
        newErrors.discount_end_date = "End date must be after start date"
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error("Error updating product. Please check the form")
      return
    }

    setIsLoading(true)

    try {
      const requestFormData = new FormData()

      // Add all form data
      for (const key in formData) {
        if (key === "is_global" || key === "product_expired_soon") {
          requestFormData.append(key, formData[key] ? 1 : 0)
        } else if (key === "discount" && !has_discount) {
          continue
        } else if (key === "discount_start_date" && !has_discount) {
          continue
        } else if (key === "discount_end_date" && !has_discount) {
          continue
        } else {
          requestFormData.append(key, formData[key])
        }
      }

      // Handle primary image
      if (primaryImage && primaryImage.file) {
        requestFormData.append("image_py", primaryImage.file)
      }

      // If existing primary image was removed
      if (primaryImageRemoved && !primaryImage?.file) {
        requestFormData.append("delete_primary_image", "true")
      }

      // Add new additional images
      const newImages = images.filter((img) => !img.isExisting && img.file)
      if (newImages.length > 0) {
        newImages.forEach((image) => {
          requestFormData.append("images[]", image.file)
        })
      }

      // Add IDs of images to be deleted
      if (deletedImageIds.length > 0) {
        requestFormData.append("deleted_image_ids", JSON.stringify(deletedImageIds))
      }

      // Add variants if variant product
      if (variantGroups.length > 0 && formData.variant === true) {
        requestFormData.append("variants", JSON.stringify(variantGroups))
      } else if (!formData.variant) {
        // Clear variants if variant checkbox is unchecked
        requestFormData.append("variants", JSON.stringify([]))
      }

      // Add clients if not global
      if (selectedClients.length > 0 && formData.is_global === false) {
        requestFormData.append("clients", JSON.stringify(selectedClients))
      } else if (formData.is_global) {
        // Clear clients if global checkbox is checked
        requestFormData.append("clients", JSON.stringify([]))
      }

      // Add product ID for update
      requestFormData.append("id", product.id)

      const response = await axios.post(`${BASE_URL}/api/products/${product.id}?_method=PUT`, requestFormData, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.data.code === 200) {
        setSuccess(true)
        toast.success("Product updated successfully!")
        setTimeout(() => {
          setSuccess(false)
        }, 3000)
      } else {
        setErrors(response.data.errors || {})
        toast.error(response.data.message || "Validation failed. Please check the form.")
      }
    } catch (error) {
      console.error("Error updating product:", error)
      toast.error(`Error updating product: ${error.message || "An unexpected error occurred."}`)
    } finally {
      setIsLoading(false)
    }
  }

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      if (primaryImage?.preview && !primaryImage.isExisting) {
        URL.revokeObjectURL(primaryImage.preview)
      }
      images.forEach((img) => {
        if (img.preview && !img.isExisting) {
          URL.revokeObjectURL(img.preview)
        }
      })
    }
  }, [primaryImage, images])

  return (
    <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
      {success && (
        <div className="bg-[var(--color-primary-500)] text-white p-6 text-center">
          <div className="flex items-center justify-center mb-2">
            <svg className="h-8 w-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-xl font-semibold">Product Updated Successfully!</span>
          </div>
          <p className="text-[var(--color-primary-50)]">Your product has been updated in the catalog.</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-8">
        <div className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-6">Basic Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label htmlFor="name" className="block font-semibold text-sm text-gray-900 mb-3">
                Product Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter product name..."
                className={`w-full py-3 px-4 outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                  errors.name
                    ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                    : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                }`}
              />
              {errors.name && <p className="mt-2 text-red-600 text-xs">{errors.name}</p>}
            </div>

            <div className="md:col-span-2">
              <label htmlFor="description" className="block font-semibold text-sm text-gray-900 mb-3">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter product description..."
                rows={4}
                className="w-full py-3 px-4 outline-none text-xs border-2 border-gray-200 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
              />
            </div>

            <div className="block grid grid-cols-1 lg:w-175 md:grid-cols-3 gap-4">
              <div className="w-full">
                <label htmlFor="fournisseur_id" className="block text-sm font-semibold text-gray-900 mb-3">
                  Fournisseur *
                </label>
                <select
                  id="fournisseur_id"
                  name="fournisseur_id"
                  value={formData.fournisseur_id}
                  onChange={handleInputChange}
                  className={`w-full py-3 px-4 cursor-pointer outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.fournisseur_id
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                >
                  <option disabled value="">
                    Select an Fournisseur
                  </option>
                  {Fournisseurs.map((Fournisseur) => (
                    <option key={Fournisseur.id} value={Fournisseur.id}>
                      {Fournisseur.name}
                    </option>
                  ))}
                </select>
                {errors.fournisseur_id && <p className="mt-2 text-red-600 text-xs">{errors.fournisseur_id}</p>}
              </div>

              <div className="">
                <label htmlFor="category_id" className="w-full block text-sm font-semibold text-gray-900 mb-3">
                  Category *
                </label>
                <select
                  id="category_id"
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleInputChange}
                  className={`w-full py-3 px-4 cursor-pointer outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.category_id
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                >
                  <option disabled value="">
                    Select a category
                  </option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.title}
                    </option>
                  ))}
                </select>
                {errors.category_id && <p className="mt-2 text-red-600 text-xs">{errors.category_id}</p>}
              </div>

              <div className="">
                <label htmlFor="status" className="block text-sm font-semibold text-gray-900 mb-3">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full py-3 px-4 cursor-pointer text-xs outline-none border-2 border-gray-200 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="out_of_stock">Out of Stock</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-6">Pricing & Inventory</h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="achat_price" className="block text-sm font-semibold text-gray-900 mb-3">
                Prix d'achat *
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">DH</span>
                <input
                  type="number"
                  id="achat_price"
                  name="achat_price"
                  value={formData.achat_price}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                  className={`w-full pl-10 py-3 px-4 outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.achat_price
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                />
              </div>
              {errors.achat_price && <p className="mt-2 text-red-600 text-xs">{errors.achat_price}</p>}
            </div>
            <div>
              <label htmlFor="saller_price" className="block text-sm font-semibold text-gray-900 mb-3">
                Prix de vendeur *
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">DH</span>
                <input
                  type="number"
                  id="saller_price"
                  name="saller_price"
                  value={formData.saller_price}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                  className={`w-full pl-10 py-3 px-4 outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.saller_price
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                />
              </div>
              {errors.saller_price && <p className="mt-2 text-red-600 text-xs">{errors.saller_price}</p>}
            </div>
            <div>
              <label htmlFor="selling_price" className="block text-sm font-semibold text-gray-900 mb-3">
                Prix de vente *
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">DH</span>
                <input
                  type="number"
                  id="selling_price"
                  name="selling_price"
                  value={formData.selling_price}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                  className={`w-full pl-10 py-3 px-4 outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.selling_price
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                />
              </div>
              {errors.selling_price && <p className="mt-2 text-red-600 text-xs">{errors.selling_price}</p>}
            </div>

            <div>
              <label htmlFor="stock" className="block text-sm font-semibold text-gray-900 mb-3">
                Stock Quantity *
              </label>
              <input
                type="number"
                id="stock"
                name="stock"
                value={formData.stock}
                onChange={handleInputChange}
                placeholder="0"
                min="0"
                className={`w-full py-3 px-4 outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                  errors.stock
                    ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                    : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                }`}
              />
              {errors.stock && <p className="mt-2 text-red-600 text-xs">{errors.stock}</p>}
            </div>
            {errors.price && <p className="text-red-600 text-xs w-100">{errors.price}</p>}
          </div>
        </div>

        <div className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-6">Product Images</h2>

          <div className="md:col-span-2 mb-6">
            <label className="block font-semibold text-sm text-gray-900 mb-3">Primary Image *</label>
            {!primaryImage ? (
              <div
                className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-200 ${
                  dragActivePrimary
                    ? "border-[var(--color-primary-500)] bg-[var(--color-primary-50)]"
                    : "border-gray-300 hover:border-[var(--color-primary-500)] hover:bg-gray-50"
                }`}
                onDragEnter={handleDragPrimary}
                onDragLeave={handleDragPrimary}
                onDragOver={handleDragPrimary}
                onDrop={handleDropPrimary}
              >
                <input
                  ref={primaryFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => e.target.files.length > 0 && handlePrimaryImageUpload(e.target.files)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div className="space-y-4">
                  <div className="w-12 h-12 mx-auto bg-[var(--color-primary-50)] rounded-full flex items-center justify-center">
                    <svg className="h-7 w-7 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Drop your primary image here</p>
                    <p className="text-gray-500 text-xs">or click to browse files</p>
                  </div>
                  <p className="text-xs text-gray-400">PNG, JPG, GIF up to 10MB</p>
                </div>
              </div>
            ) : (
              <div className="relative bg-white border-2 border-gray-200 rounded-2xl overflow-hidden">
                <div className="aspect-square relative">
                  <img
                    src={primaryImage.preview || "/placeholder.svg"}
                    fill
                    className="object-cover"
                    alt=""
                    onError={(e) => {
                      e.target.src = "/placeholder.svg"
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={removePrimaryImage}
                  className="absolute cursor-pointer top-2 right-2 bg-red-500 text-white p-2 rounded-full shadow-lg hover:bg-red-600 transition-all duration-200"
                >
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
            {errors.primaryImage && <p className="mt-2 text-red-600 text-xs">{errors.primaryImage}</p>}
          </div>

          <div
            className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-200 mb-6 ${
              dragActiveAdditional
                ? "border-[var(--color-primary-500)] bg-[var(--color-primary-50)]"
                : "border-gray-300 hover:border-[var(--color-primary-500)] hover:bg-gray-50"
            }`}
            onDragEnter={handleDragAdditional}
            onDragLeave={handleDragAdditional}
            onDragOver={handleDragAdditional}
            onDrop={handleDropAdditional}
          >
            <input
              ref={additionalFileInputRef}
              type="file"
              multiple
              onChange={(e) => e.target.files.length > 0 && handleAdditionalImageUpload(e.target.files)}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />

            <div className="space-y-4">
              <div className="w-12 h-12 mx-auto bg-[var(--color-primary-50)] rounded-full flex items-center justify-center">
                <svg className="h-7 w-7 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </div>
              <div>
                <p className=" font-medium text-gray-900">Drop your additional images here</p>
                <p className="text-gray-500 text-xs">or click to browse files</p>
              </div>
              <p className="text-xs text-gray-400">PNG, JPG, GIF up to 10MB each</p>
            </div>
          </div>

          {images.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {images.map((image) => (
                <div key={image.id} className="relative bg-white border-2 border-gray-200 rounded-2xl overflow-hidden">
                  <div className="aspect-square relative">
                    <img
                      src={image.preview || "/placeholder.svg"}
                      fill
                      className="object-cover"
                      alt=""
                      onError={(e) => {
                        e.target.src = "/placeholder.svg"
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeImage(image.id)}
                    className="absolute cursor-pointer top-2 right-2 bg-red-500 text-white p-2 rounded-full shadow-lg hover:bg-red-600 transition-all duration-200"
                  >
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="md:col-span-2 mb-6">
          <label htmlFor="image_urls" className="block font-semibold text-sm text-gray-900 mb-3">
            Additional Image URLs (comma-separated)
          </label>
          <input
            type="text"
            id="image_urls"
            name="image_urls"
            value={formData.image_urls}
            onChange={handleInputChange}
            placeholder="Enter additional image URLs, e.g., url1.jpg, url2.png"
            className={`w-full py-3 px-4 outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
              errors.image_urls
                ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
            }`}
          />
          {errors.image_urls && <p className="mt-2 text-red-600 text-xs">{errors.image_urls}</p>}
        </div>

        <div className="mb-8">
          <div className="my-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="variant"
                checked={formData.variant}
                onChange={handleInputChange}
                className="appearance-none cursor-pointer w-9 focus:outline-none checked:bg-[var(--color-primary-200)]  h-5 bg-gray-300 rounded-full before:inline-block before:rounded-full checked:before:bg-[var(--color-primary-500)] before:bg-gray-500 before:h-4 before:w-4 checked:before:translate-x-full shadow-inner transition-all duration-300 before:mt-0.5 before:ml-0.5"
              />
              <span className="ml-3 font-medium text-sm text-gray-900">
                Variant Product
                <span className="block text-xs text-gray-500">Available to all clients by default</span>
              </span>
            </label>
          </div>
          {formData.variant && (
            <div>
              <div className="mb-4">
                <h3 className="text-sm font-bold mb-2">Variant Types:</h3>
                <div className="flex gap-2 mb-4">
                  {["color", "size", "material"].map((type) => {
                    const options = availableVariants.filter((v) => v.type === type)
                    if (options.length === 0) return null

                    const isSelected = selectedVariantTypes.includes(type)

                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => (isSelected ? removeVariantType(type) : addVariantType(type))}
                        className={`px-3 py-1.5 text-xs cursor-pointer rounded-xl font-medium transition-all duration-200 ${
                          isSelected ? "bg-[var(--color-primary-500)] text-white" : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {type}
                      </button>
                    )
                  })}
                </div>
              </div>

              {variantGroups.map((group, index) => (
                <div key={group.id} className="mb-4 border border-gray-300 py-2 px-4 rounded-xl">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-sm font-bold">Group {index + 1}</h3>
                    {variantGroups.length > 1 && (
                      <button onClick={() => removeGroup(group.id)} className="text-red-500 text-xs cursor-pointer">
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>

                  {selectedVariantTypes.map((type) => {
                    const options = availableVariants.filter((v) => v.type === type)
                    if (options.length === 0) return null

                    return (
                      <div key={type} className="mb-2">
                        <label className="text-xs font-medium block mb-1 capitalize">{type}</label>
                        <div className="flex gap-2 flex-wrap">
                          {options.map((variant) => {
                            const isSelected = group.selectedVariants[type] === variant.id

                            return type === "color" ? (
                              <button
                                key={variant.id}
                                type="button"
                                onClick={() => updateGroupVariant(group.id, type, variant.id)}
                                className={`p-1 pb-[0.1px] cursor-pointer rounded-xl transition-all duration-200 ${
                                  isSelected
                                    ? "ring-2 ring-[var(--color-primary-500)] ring-offset-2"
                                    : "hover:ring-2 hover:ring-gray-300 hover:ring-offset-2"
                                }`}
                                title={variant.name || variant.value}
                              >
                                <span
                                  className="inline-block w-10 h-10 rounded-lg border border-gray-300"
                                  style={{ backgroundColor: variant.value }}
                                />
                              </button>
                            ) : (
                              <button
                                key={variant.id}
                                type="button"
                                onClick={() => updateGroupVariant(group.id, type, variant.id)}
                                className={`px-3 flex items-center cursor-pointer text-xs py-1.5 rounded-xl font-medium transition-all duration-200 ${
                                  isSelected
                                    ? "bg-[var(--color-primary-500)] border-2 border-[var(--color-primary-500)] text-white hover:bg-[var(--color-primary-400)] hover:border-[var(--color-primary-500)]"
                                    : "bg-white text-gray-700 border-2 border-gray-200 hover:border-[var(--color-primary-500)]"
                                }`}
                              >
                                {variant.value}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ))}

              <button
                onClick={addGroup}
                type="button"
                className="px-4 py-2 cursor-pointer bg-[var(--color-primary-500)] text-white rounded-xl text-sm"
                disabled={selectedVariantTypes.length === 0}
              >
                + Add Group
              </button>
            </div>
          )}
        </div>

        <div className="my-8">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="is_global"
              checked={formData.is_global}
              onChange={handleInputChange}
              className="appearance-none cursor-pointer w-9 focus:outline-none checked:bg-[var(--color-primary-200)]  h-5 bg-gray-300 rounded-full before:inline-block before:rounded-full checked:before:bg-[var(--color-primary-500)] before:bg-gray-500 before:h-4 before:w-4 checked:before:translate-x-full shadow-inner transition-all duration-300 before:mt-0.5 before:ml-0.5"
            />
            <span className="ml-3 font-medium text-sm text-gray-900">
              Global Product
              <span className="block text-xs text-gray-500">Available to all clients by default</span>
            </span>
          </label>
        </div>

        {!formData.is_global && (
          <div className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-6">Client Assignment</h2>
            <div className="bg-gray-50 rounded-2xl p-6">
              <p className="text-gray-600 mb-4 text-xs">Select which clients can access this product:</p>

              <div className="mt-4">
                <Select
                  instanceId={selectId}
                  isMulti
                  options={availableClients.map((client) => ({
                    value: client.id,
                    label: `${client.info.name} (${client.info.name_store})`,
                  }))}
                  value={availableClients
                    .filter((client) => selectedClients.includes(client.id))
                    .map((client) => ({
                      value: client.id,
                      label: `${client.info.name} (${client.info.name_store})`,
                    }))}
                  onChange={(selectedOptions) => {
                    setSelectedClients(selectedOptions.map((option) => option.value))
                  }}
                  className={`w-full py-0.5 px-1 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]`}
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      border: "none",
                      cursor: "pointer",
                    }),
                  }}
                  placeholder="Search and select clients..."
                  noOptionsMessage={() => "No clients found"}
                  isSearchable
                />

                {errors.clients && <p className="mt-2 text-red-600 text-xs">{errors.clients}</p>}

                {/* Show selected clients as chips */}
                {selectedClients.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {availableClients
                      .filter((client) => selectedClients.includes(client.id))
                      .map((client) => (
                        <div
                          key={client.id}
                          className="flex items-center text-[var(--color-primary-500)] bg-[var(--color-primary-50)] border border-[var(--color-primary-500)] rounded-full px-3 text-[11px]"
                        >
                          <span>
                            {client.info.name} ({client.info.name_store})
                          </span>
                          <button
                            type="button"
                            onClick={() => setSelectedClients((prev) => prev.filter((id) => id !== client.id))}
                            className="ml-2 text-lg text-[var(--color-primary-500)] hover:text-[var(--color-primary-50)]0 cursor-pointer"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mb-10">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="product_expired_soon"
              checked={formData.product_expired_soon}
              onChange={handleInputChange}
              className="appearance-none cursor-pointer w-9 focus:outline-none checked:bg-[var(--color-primary-200)] h-5 bg-gray-300 rounded-full before:inline-block before:rounded-full checked:before:bg-[var(--color-primary-500)] before:bg-gray-500 before:h-4 before:w-4 checked:before:translate-x-full shadow-inner transition-all duration-300 before:mt-0.5 before:ml-0.5"
            />
            <span className="ml-3 font-medium text-sm text-gray-900">Product Expired Soon</span>
          </label>
        </div>

        <div className="mb-12">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="has_discount"
              checked={has_discount}
              onChange={() => {
                setHas_discount((prev) => !prev)
              }}
              className="appearance-none cursor-pointer w-9 focus:outline-none checked:bg-[var(--color-primary-200)] h-5 bg-gray-300 rounded-full before:inline-block before:rounded-full checked:before:bg-[var(--color-primary-500)] before:bg-gray-500 before:h-4 before:w-4 checked:before:translate-x-full shadow-inner transition-all duration-300 before:mt-0.5 before:ml-0.5"
            />
            <span className="ml-3 font-medium text-sm text-gray-900">Apply Discount</span>
          </label>

          {has_discount && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="">
                <label htmlFor="discount" className="block font-semibold text-sm text-gray-900 mb-3">
                  Discount (%)
                </label>
                <input
                  type="number"
                  min="0"
                  id="discount"
                  name="discount"
                  value={formData.discount}
                  onChange={handleInputChange}
                  placeholder="Enter discount"
                  className={`w-full py-3 px-4 outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.discount
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                />
                {errors.discount && <p className="mt-2 text-red-600 text-xs">{errors.discount}</p>}
              </div>
              <div>
                <label htmlFor="discount_start_date" className="block text-sm font-semibold text-gray-900 mb-3">
                  Discount Start Date *
                </label>
                <input
                  type="date"
                  id="discount_start_date"
                  name="discount_start_date"
                  value={formData.discount_start_date}
                  onChange={handleInputChange}
                  className={`w-full py-3 px-4 outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.discount_start_date
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                />
                {errors.discount_start_date && (
                  <p className="mt-2 text-red-600 text-xs">{errors.discount_start_date}</p>
                )}
              </div>
              <div>
                <label htmlFor="discount_end_date" className="block text-sm font-semibold text-gray-900 mb-3">
                  Discount End Date *
                </label>
                <input
                  type="date"
                  id="discount_end_date"
                  name="discount_end_date"
                  value={formData.discount_end_date}
                  onChange={handleInputChange}
                  className={`w-full py-3 px-4 outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.discount_end_date
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                />
                {errors.discount_end_date && <p className="mt-2 text-red-600 text-xs">{errors.discount_end_date}</p>}
              </div>
            </div>
          )}
        </div>

        <div className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-6">Product Rating</h2>
          <div>
            <label htmlFor="rating" className="block text-sm font-semibold text-gray-900 mb-3">
              Rating (1 to 5 Stars)
            </label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <svg
                  key={star}
                  className={`h-6 w-6 cursor-pointer ${star <= formData.rating ? "text-yellow-400" : "text-gray-300"}`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  onClick={() => setFormData({ ...formData, rating: star })}
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.683-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.565-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
              <span className="ml-2 text-sm text-gray-600">
                {formData.rating > 0 ? `${formData.rating} star${formData.rating > 1 ? "s" : ""}` : "No rating"}
              </span>
            </div>
          </div>
        </div>

        <div className="flex sm:flex-row gap-4 pt-6 border-t border-gray-200">
          <Link
            href="/products"
            className="block cursor-pointer text-center flex-1 py-3 px-4 cursor-pointer text-sm border-2 border-gray-200 text-gray-700 font-semibold rounded-2xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-200"
          >
            <button type="button" disabled={isLoading} className="cursor-pointer">
              Annuler
            </button>
          </Link>
          <button
            type="submit"
            disabled={isLoading}
            className={`flex-1 py-3 px-4 text-sm cursor-pointer font-semibold rounded-2xl transition-all duration-200 ${
              isLoading
                ? "bg-gray-400 text-white cursor-not-allowed"
                : "bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white shadow-lg hover:shadow-xl transform hover:scale-105"
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Updating...
              </span>
            ) : (
              <span className="flex items-center justify-center">
                Update Product
                <svg className="h-5 w-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
